/**
 * wsProtocol — Phase 5 (P3-40) + match browser (P6-08) WebSocket message contract.
 * Pure types + encode/decode. No sockets, DOM, or Node net APIs.
 */

import { pack, unpack } from 'msgpackr';

export const PROTOCOL_VERSION = 1;

/**
 * Documented CMD_CAST payload keys (SPY-12).
 * Required: key or slot. Optional aim/target/dir for lag-tolerant casts.
 */
export const CMD_CAST_PAYLOAD_FIELDS = Object.freeze([
  'key',
  'slot',
  'x',
  'z',
  'targetId',
  'dirX',
  'dirZ',
  'angle'
]);

/**
 * Build a CMD_CAST payload; omits undefined/null optional fields (legacy-safe).
 * @param {{
 *   key?: string,
 *   slot?: string,
 *   x?: number,
 *   z?: number,
 *   targetId?: string,
 *   dirX?: number,
 *   dirZ?: number,
 *   angle?: number
 * }} fields
 */
export function createCastCmdPayload(fields = {}) {
  const out = {};
  if (fields.key !== undefined && fields.key !== null && fields.key !== '') out.key = fields.key;
  if (fields.slot !== undefined && fields.slot !== null && fields.slot !== '') out.slot = fields.slot;
  if (fields.x !== undefined && fields.x !== null) out.x = fields.x;
  if (fields.z !== undefined && fields.z !== null) out.z = fields.z;
  if (fields.targetId !== undefined && fields.targetId !== null && fields.targetId !== '') {
    out.targetId = fields.targetId;
  }
  if (fields.dirX !== undefined && fields.dirX !== null) out.dirX = fields.dirX;
  if (fields.dirZ !== undefined && fields.dirZ !== null) out.dirZ = fields.dirZ;
  if (fields.angle !== undefined && fields.angle !== null) out.angle = fields.angle;
  return out;
}

/** All wire message type strings (client + server). */
export const WS_MSG = Object.freeze({
  // Session
  HELLO: 'hello',
  WELCOME: 'welcome',
  PING: 'ping',
  PONG: 'pong',
  ERROR: 'error',

  // Lobby room (P3-42)
  JOIN_ROOM: 'join_room',
  LEAVE_ROOM: 'leave_room',
  ROOM_STATE: 'room_state',
  PLAYER_READY: 'player_ready',
  LOCK_IN: 'lock_in',
  MATCH_START: 'match_start',

  // Match browser (P6-08)
  LIST_ROOMS: 'list_rooms',
  CREATE_ROOM: 'create_room',
  START_MATCH: 'start_match',
  FILL_BOTS: 'fill_bots',
  SET_TEAM: 'set_team',
  ROOM_LIST: 'room_list',

  // In-match commands (P3-43)
  CMD_MOVE: 'cmd_move',
  CMD_ATTACK: 'cmd_attack',
  /**
   * cmd_cast payload (SPY-12):
   * { key|slot, x?, z?, targetId?, dirX?, dirZ?, angle? }
   * Aim (x/z), targetId, and dir/angle are optional for legacy clients.
   */
  CMD_CAST: 'cmd_cast',
  CMD_STOP: 'cmd_stop',

  // Authoritative sync (P3-43)
  STATE_SNAPSHOT: 'state_snapshot',
  STATE_DELTA: 'state_delta',
  CMD_ACK: 'cmd_ack'
});

/** Room list visibility (create_room / room_list). */
export const ROOM_VISIBILITY = Object.freeze({
  PUBLIC: 'public',
  PRIVATE: 'private'
});

/** LobbyRoom.phase values. */
export const ROOM_PHASE = Object.freeze({
  LOBBY: 'lobby',
  MATCH_STARTING: 'match_starting',
  IN_PROGRESS: 'in_progress'
});

/** Common `error.payload.code` values (match browser + lobby). */
export const WS_ERROR_CODES = Object.freeze({
  BAD_PASSWORD: 'bad_password',
  ROOM_FULL: 'room_full',
  ROOM_NOT_FOUND: 'room_not_found',
  NOT_HOST: 'not_host',
  ALREADY_STARTED: 'already_started',
  TEAM_FULL: 'team_full',
  NOT_ALLOWED: 'not_allowed',
  BAD_TEAM: 'bad_team'
});

export const CLIENT_MSG_TYPES = Object.freeze([
  WS_MSG.HELLO,
  WS_MSG.PING,
  WS_MSG.JOIN_ROOM,
  WS_MSG.LEAVE_ROOM,
  WS_MSG.PLAYER_READY,
  WS_MSG.LOCK_IN,
  WS_MSG.LIST_ROOMS,
  WS_MSG.CREATE_ROOM,
  WS_MSG.START_MATCH,
  WS_MSG.FILL_BOTS,
  WS_MSG.SET_TEAM,
  WS_MSG.CMD_MOVE,
  WS_MSG.CMD_ATTACK,
  WS_MSG.CMD_CAST,
  WS_MSG.CMD_STOP
]);

export const SERVER_MSG_TYPES = Object.freeze([
  WS_MSG.WELCOME,
  WS_MSG.PONG,
  WS_MSG.ERROR,
  WS_MSG.ROOM_STATE,
  WS_MSG.MATCH_START,
  WS_MSG.ROOM_LIST,
  WS_MSG.STATE_SNAPSHOT,
  WS_MSG.STATE_DELTA,
  WS_MSG.CMD_ACK
]);

const KNOWN_TYPES = new Set([...CLIENT_MSG_TYPES, ...SERVER_MSG_TYPES]);

/**
 * @param {string} type
 * @param {object} [payload]
 * @param {number} [seq]
 */
export function createEnvelope(type, payload = {}, seq = 0) {
  return {
    v: PROTOCOL_VERSION,
    type,
    seq,
    payload: payload && typeof payload === 'object' ? payload : {}
  };
}

export function isKnownMessageType(type) {
  return KNOWN_TYPES.has(type);
}

/**
 * @returns {{ ok: true, envelope: object } | { ok: false, error: string }}
 */
export function validateEnvelope(raw) {
  if (!raw || typeof raw !== 'object') {
    return { ok: false, error: 'envelope_not_object' };
  }
  if (raw.v !== PROTOCOL_VERSION) {
    return { ok: false, error: 'bad_version' };
  }
  if (typeof raw.type !== 'string' || !isKnownMessageType(raw.type)) {
    return { ok: false, error: 'unknown_type' };
  }
  if (typeof raw.seq !== 'number' || !Number.isFinite(raw.seq) || raw.seq < 0) {
    return { ok: false, error: 'bad_seq' };
  }
  if (!raw.payload || typeof raw.payload !== 'object' || Array.isArray(raw.payload)) {
    return { ok: false, error: 'bad_payload' };
  }
  return { ok: true, envelope: raw };
}

/** Identity parse for already-decoded objects (validates). */
export function parseEnvelope(raw) {
  const result = validateEnvelope(raw);
  return result.ok ? result.envelope : null;
}

export function encodeMessage(envelope) {
  return JSON.stringify(envelope);
}

export function encodeMessageBinary(envelope) {
  return pack(envelope);
}

/**
 * Decodes JSON string or MessagePack binary buffer/Uint8Array/ArrayBuffer.
 * @param {string|Uint8Array|ArrayBuffer|Buffer} input
 * @returns {{ ok: true, envelope: object } | { ok: false, error: string }}
 */
export function decodeMessage(input) {
  if (!input) {
    return { ok: false, error: 'not_string' };
  }

  let raw;
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input);
    } catch {
      return { ok: false, error: 'invalid_json' };
    }
  } else if (input instanceof Uint8Array || input instanceof ArrayBuffer) {
    try {
      const bytes = input instanceof ArrayBuffer ? new Uint8Array(input) : input;
      raw = unpack(bytes);
    } catch {
      return { ok: false, error: 'invalid_msgpack' };
    }
  } else {
    return { ok: false, error: 'not_string' };
  }

  return validateEnvelope(raw);
}

export function decodeMessageBinary(bytes) {
  return decodeMessage(bytes);
}

